import DateHelper from '../../../Utility/DateHelper';
import { Random } from '../../../Utility/Random';
import { HelperFriendStatus } from './HelperFriendStatus';
import { IFriendData } from '../../../Player/PlayerProperty/Social/IPlayerSocialData';
import { LevelType } from '../../Player/Progress/PlayerLevels';
import { IGameState } from '../../Game/IGameState';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import { SocialStateHelper } from 'State/Player/Social/SocialStateHelper';
import { isArray } from 'lodash';

let forceHelperFriend = false;

export function setForceHelperFriend(force: boolean = true): void {
    forceHelperFriend = force;
}

export function checkHelperFriendVisible(state: IGameState): IGameState {
    // Must be at least at level 15.
    if (state.levelState.levelNumber < 15) return state;

    let helperFriendStatus: HelperFriendStatus = state.levelState.helperFriendStatus;

    // If helper-friends are disabled for this level then do nothing.
    if (helperFriendStatus === HelperFriendStatus.DISABLED || helperFriendStatus === HelperFriendStatus.USED) {
        return state;
    }

    // Helper-friends is only available if 1 to 5 cards remain in the players stack.
    if (state.levelState.cardsState.inStack.length <= 5 && state.levelState.cardsState.inStack.length > 0) {
        if (helperFriendStatus === HelperFriendStatus.ENABLED) {
            helperFriendStatus = HelperFriendStatus.SHOW;
        }
    } else {
        // If helper-friends was visible and not used then it be set back to 'ENABLED'.
        // It will then re-appear if the user has 1 to 5 cards again.
        if (helperFriendStatus === HelperFriendStatus.SHOW) {
            helperFriendStatus = HelperFriendStatus.ENABLED;
        }
    }

    return {
        ...state,
        levelState: {
            ...state.levelState,
            helperFriendStatus: helperFriendStatus,
        },
    };
}

export function checkHelperFriendEnabled(state: IGameState): IGameState {
    // Check if friends are available
    if (
        // If no friends
        !isArray(state.playerState.social.friendData) ||
        // If an empty array
        state.playerState.social.friendData.length === 0 ||
        // Or if all friends are on cooldown
        !state.playerState.social.friendData.some((friend: IFriendData) => !friend.helperTimer)
    ) {
        // Then disable the helper friend for the level
        return {
            ...state,
            levelState: {
                ...state.levelState,
                helperFriendStatus: HelperFriendStatus.DISABLED,
            },
        };
    }

    // Otherwise calculate the percentage chance based on the number of level attempts.
    const currentLevel = state.playerState.levels[LevelType.Core].find(
        level => level.levelNumber === state.levelState.levelNumber
    );

    // 10% chance per attempt on this level since, per session, since helper-friend was last used.
    const percentageChance = Math.min(0.4, (currentLevel.helperFriendAttempts - 1) * 0.1);

    const random: number = Random.value();

    if (forceHelperFriend || random <= percentageChance) {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                helperFriendStatus: HelperFriendStatus.ENABLED,
            },
        };
    }

    return {
        ...state,
        levelState: {
            ...state.levelState,
            helperFriendStatus: HelperFriendStatus.DISABLED,
        },
    };
}

/**
 * Handle a successful 'help friend'.
 *
 * The cooldown validation for helper-friends is currently done in `LeaderboardHelper.getFriendForHelp()`.
 * This is because new 'unknown' friends are not initialized into the state when the game is loaded.
 * TODO: Move all 'platform friends' to state during initialization/preload.
 *
 * @param state
 * @param friendId The playerId of the friend.
 */
export function helpFriend(state: IGameState, friendId: string): IGameState {
    // Reset helper-friend attempts for this level
    state = ProgressStateHelper.updateLevel(state, state.levelState.levelNumber, {
        helperFriendAttempts: 0,
    });

    const { playerState, friendData } = SocialStateHelper.getFriendData(state.playerState, friendId);

    return {
        ...state,
        playerState: {
            ...playerState,
            social: {
                ...state.playerState.social,
                friendData: playerState.social.friendData.map((friend: IFriendData) => {
                    if (friend.playerId !== friendId) {
                        return friend;
                    }
                    return {
                        ...friend,
                        helperTimer: DateHelper.now.getTime(),
                    };
                }),
            },
        },
        levelState: {
            ...state.levelState,
            helperFriendStatus: HelperFriendStatus.USED,
        },
    };
}
